import Vue from 'vue';
import i18n from '@/lang/_i18n';
import store from '@/store';

import { mapState, mapGetters, mapActions } from 'vuex';

import vocabularyMixin from '@/mixins/vocabulary';
import DictionaryTooltip from '@/components/Dictionary/Tooltip';
import { getApiMethod } from '@/modules/apiSelector'

export default {
  mixins: [vocabularyMixin],
  data() {
    return {
      tooltipInstance: null,
      element: null,
      tooltip: null,
      words: null
    };
  },
  computed: {
    ...mapState('vocabulary', [
      'categorizedWords',
      'categoryLists',
      'listCategoryId',
      'modalCategorizedWords',
      'modalCategoryId',
      'modalItemPerPage'
    ]),
    ...mapState(['programId']),
    ...mapState('material', ['articleId']),
    ...mapState('htmlMaterial', ['htmlLessonId']),
    ...mapGetters(['isDiscoveryLesson', 'isCorpLesson', 'isStudent', 'isZipanService']),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId']),

    programLessonId() {
      return this.isDiscoveryLesson ? this.articleId : this.htmlLessonId;
    },

    enableVocabularyTooltip() {
      return this.isStudent && !this.isCorpLesson && !this.isTaiwanCountryId && !this.isZipanService;
    }
  },
  methods: {
    ...mapActions('vocabulary', ['getMenuCategorizedWords']),

    // Fetch all Words from api to highlight all saved words
    async highlightWords() {
      await this.fetchCategorizedWords();
      const { hits } = this.categorizedWords;
      this.words = hits.map(item => {
        return (item.word).toLowerCase();
      });
    },

    /**
     * This will translate all the html/collection to check all the words
     *
     * @param {htmlcollection|htmlbody} html
     * @param {boolean} isCollection
     */
    async initializedTooltip(html, isCollection = true) {
      if (!html.length || !this.enableVocabularyTooltip) {
        return;
      }

      await this.highlightWords();

      let elements = [];
      if (isCollection) {
        for (let i = 0; i < html.length; i++) {
          let element = html[i];
          element.innerHTML = element.innerHTML.replaceAll(/<br\s*\/?>/gi, '@@@');
          this.wrapWordsRecursively(element);
          element.innerHTML = element.innerHTML.replaceAll('@@@','<br/>');
        }
      } else {
        this.setHtmlBody(html);
      }

      // pass only new elements to avoid duplication of listener
      const vocabularyContainer = document.getElementsByClassName('vocabulary-container');
      const vocabularyElements = elements.length ? elements : vocabularyContainer;
      this.initTooltipEvent(vocabularyElements);
    },
    // set html body
    setHtmlBody(htmlBody) {
      const excludeTags = new RegExp('(<.+?>|&\\w+;)');
      const htmlParts = htmlBody.split(excludeTags).filter(Boolean);
      const innerHtml = htmlParts
        .map(item =>
          excludeTags.test(item)
            ? item
            : item.replace(/(\w+)/gi, word => {
                return this.vocabularyReplace(word);
              })
        )
        .join('');
      const newHtml = document.querySelector('.html-content');
      newHtml.innerHTML = innerHtml;
    },
    initTooltipEvent(vocabularyContainer) {
      for (let element of vocabularyContainer) {
        element.addEventListener('mouseenter', () => {
          if (element.innerText.length > 3) {
            element.classList.add('target');
          }
        });

        element.addEventListener('click', async () => {
          if (element.innerText.length <= 3 || element.classList.contains('background')) {
            return false;
          }

          let activeWord = document.querySelectorAll('.vocabulary-container.background');
          if (activeWord) {
            activeWord.forEach(word => {
              this.hideActiveTooltip(word);
            });
          }

          // Fetch translation and do element manipulation
          const translation = await this.setTranslation(element);

          // Save translation
          if (translation) {
            if (!translation.recordExist) {
              const save = element.querySelector('.save-btn');
              if (save) {
                save.addEventListener('click', async () => {
                  const result = await this.saveTranslation(element, translation);
                  if (!result.id) {
                    return;
                  }
                });
              }
            }
          }
        });
      }
    },

    /**
     * Recursively wrap text nodes with vocabulary spans
     * @param {HTMLElement} element
     */
    wrapWordsRecursively(element) {
      for (const node of Array.from(element.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          const wrappedHTML = node.textContent.replace(/\b(\w+)\b/g, word =>
            this.vocabularyReplace(word)
          );
          const wrapper = document.createElement('span');
          wrapper.innerHTML = wrappedHTML;
          node.replaceWith(...wrapper.childNodes);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          this.wrapWordsRecursively(node);
        }
      }
    },

    /**
     * Create vocabulary tag for matching words
     * @param {string} word
     * @returns {string} HTML string
     */
    vocabularyReplace(word) {
      const highlightClass = this.words.includes((word).toLowerCase()) ? 'highlight' : '';
      return `<vocabulary class='vocabulary-container vocabulary-${word.toLowerCase()} ${highlightClass}'>${word}</vocabulary>`;
    },

    // Fetch translation and element manipulation
    async setTranslation(element) {
      this.element = element;
      this.element.classList.add('background');
      const dictionary = await this.fetchTranslation(this.element);
      if (!dictionary) {
        return;
      }

      // filtered data with no meaning
      const filteredData = dictionary.dictionary.filter(item => item.meaning.length);
      if (!filteredData.length) {
        this.showNotification('Notice', this.$t('vocabulary.tooltip.noMeaning'), 'warning');
        this.element.classList.remove('background');
        this.element.classList.remove('target');
        return;
      }

      // remove transform style of active word
      const parent = element.closest('div');
      parent.style.transform = 'none';

      this.setTableOverflowAsVisible(true);

      if (!element.children.length) {
        this.appendDictionaryTooltip(element, dictionary);
        const tooltipContent = document.querySelector('.tooltip-content');
        this.setManualStyle(tooltipContent);
      }
      return dictionary;
    },

    appendDictionaryTooltip(element, dictionary) {
      if (this.tooltipInstance) {
        this.tooltipInstance = null;
      }

      const tempSpan = document.createElement('span');
      element.appendChild(tempSpan);

      this.tooltipInstance = new Vue({
        i18n,
        store,
        render: h => h(DictionaryTooltip, { props: { dictionary } })
      }).$mount(tempSpan);

      this.$nextTick(() => {
        document.addEventListener('click', this.setTooltipHide);
      });
    },
    // create loader element
    createLoaderContent(element) {
      if (!element) return false;

      let loader = document.createElement('div');
      let loading = document.createElement('div');
      loader.classList.add('html-material-loader');
      loading.classList.add('html-material-loading');
      loader.appendChild(loading);
      element.appendChild(loader);
    },
    // fetch translation word
    async fetchTranslation(element) {
      let translation = {};
      const sourceText = element.innerText;
      if (sourceText.split('\n').length > 1) return false;

      this.createLoaderContent(element);
      const apiMethod = getApiMethod('getTranslation');
      
      translation = await apiMethod(sourceText, this.programLessonId, this.programId);
      if (element.children.length) {
        element.removeChild(element.children[0]);
      }

      return translation;
    },
    // save translation
    async saveTranslation(element, translation) {
      if (this.isCorpLesson) {
        return false;
      }

      if (!Object.keys(translation).length && !element.children.length) {
        return false;
      }

      const params = {
        lesson_id: this.programLessonId,
        program_id: this.programId,
        word: translation.original
      };

      const isTranslationSaved = element.querySelector('.indicator.active');
      if (!isTranslationSaved) {
        const apiMethod = getApiMethod('storeVocabulary');
    
        try {
          console.log('[saveTranslation] Calling API method...');
          const response = await apiMethod(
            params.word,
            params.lesson_id,
            params.program_id
          );
    
          if (!response || response.result === undefined) {
            console.warn('[saveTranslation] Invalid or missing response.result');
            return;
          }
    
          params.id = response.result;

          this.fetchCategorizedWords();
    
          const saveBtn = element.querySelector('.save-btn');
          if (saveBtn) {
            saveBtn.innerHTML =
              '<img src=' +
              require('@/assets/img/check_circle.svg') +
              ' /> ' +
              this.$t('vocabulary.tooltip').savedLabel;
            element.querySelector('.indicator')?.classList.add('active');
            saveBtn.classList.add('word-added');
          }
          const wordElements = document.querySelectorAll(
            `.vocabulary-container.vocabulary-${translation.original.toLowerCase()}`
          );
          wordElements?.forEach(word => word.classList.add('highlight'));
    
        } catch (error) {
          console.error('[saveTranslation] Error during API call:', error);
        }
      } else {
        console.log('[saveTranslation] Translation already saved, skipping API call.');
      }

      return params;
    },

    showNotification(title, message, type, position = 'top-left') {
      this.$notify({
        title,
        message,
        type,
        position,
        duration: 3000
      });
    },

    // set the manual style if reach the left/right side
    setManualStyle(element) {
      const className = this.isDiscoveryLesson ? 'html-content' : 'html-lesson-wrapper';
      const parentElement = document.getElementsByClassName(className)[0];
      if (!parentElement) {
        return;
      }

      const materialOffset = parentElement.getBoundingClientRect();
      const tooltipOffset = element.getBoundingClientRect();

      if (tooltipOffset.left < materialOffset.left) {
        element.style.left = '68px';
      }

      if (tooltipOffset.right >= materialOffset.right) {
        const adjustment = parentElement.offsetWidth < 360 ? 10 : -5;
        const difference = adjustment - (tooltipOffset.right - materialOffset.right);
        element.style.left = difference.toFixed(0) + 'px';
      }

      // set tooltip to bottom if reach parent bottom
      if (tooltipOffset.top < materialOffset.top) {
        element.style.bottom = '0%';
        element.style.marginTop = '5px';
        element.style.top = '100%';
      }
    },
    setTooltipHide(e) {
      const tooltipElement = document.querySelector('.tooltip-content');
      if (!tooltipElement) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (
        this.isClickedOutsideTooltip(e, tooltipElement) ||
        e.target.classList.contains('el-icon-close')
      ) {
        this.hideTooltip();
      }
    },

    isClickedOutsideTooltip(e, tooltipElement) {
      return tooltipElement !== e.target && !tooltipElement.contains(e.target);
    },

    hideTooltip() {
      this.tooltip = null;
      if (this.tooltipInstance) {
        this.tooltipInstance.$destroy();
        this.tooltipInstance = null;
      }

      document.removeEventListener('click', this.setTooltipHide);

      this.element.classList.remove('background');
      this.element.classList.remove('target');
      this.element.closest('div').style.transform = '';
      this.setTableOverflowAsVisible(false);

      if (this.element.children.length) {
        this.element.removeChild(this.element.children[0]);
      }

      /**
       * In case there is a previous opened accordion after hiding the tooltip,
       * We need to recalculate previously set height so footer will not be cut
       */
    },

    hideActiveTooltip(word) {
      if (this.tooltipInstance) {
        this.tooltipInstance.$destroy();
        this.tooltipInstance = null;
      }

      word.classList.remove('background');
      word.classList.remove('target');

      if (word.children.length) {
        word.removeChild(word.children[0]);
      }
    },

    async fetchCategorizedWords() {
      const lesson_id = this.programLessonId;
      const params = { lesson_id };
      await this.getMenuCategorizedWords(params);
    },

    // override table overflow to show tooltip
    setTableOverflowAsVisible(state) {
      const tableScroll = this.element.closest('.table-responsive');
      const cmsBasictableScroll = this.element.closest('.basic-table-container');
      const cmsEvaluationtableScroll = this.element.closest('.evaluation-table-container');
      if (tableScroll) {
        tableScroll.style.setProperty('overflow', state ? 'visible' : 'auto', 'important');
      }
      if (cmsBasictableScroll) {
        cmsBasictableScroll.style.setProperty('overflow', state ? 'visible' : 'hidden', 'important');
      }
      if (cmsEvaluationtableScroll) {
        cmsEvaluationtableScroll.style.setProperty('overflow', state ? 'visible' : 'hidden', 'important');
      }
    }
  }
};
